import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useEvents Hook
 * Fetches events from Supabase with dynamic filtering
 * 
 * @param {Object} filters - Filter criteria
 * @param {string} filters.search - Text search (searches title ILIKE)
 * @param {string} filters.city - City filter
 * @param {string} filters.difficulty - Difficulty filter (Beginner, Intermediate, Pro)
 * @param {string} filters.dateFrom - Start date filter
 * @param {string} filters.dateTo - End date filter
 * @param {number} filters.maxPrice - Maximum price filter
 * 
 * @returns {Object} { events, loading, error }
 */
const useEvents = (filters = {}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFilteredEvents = async () => {
      try {
        setLoading(true);
        setError(null);

        // Start building query
        let query = supabase?.from('events')?.select('*')?.eq('status', 'published')?.order('created_at', { ascending: false });

        // Apply text search (ILIKE for case-insensitive search)
        if (filters?.search && filters?.search?.trim() !== '') {
          query = query?.ilike('title', `%${filters?.search}%`);
        }

        // Apply city filter
        if (filters?.city && filters?.city !== 'all') {
          query = query?.eq('city', filters?.city);
        }

        // Apply difficulty filter
        if (filters?.difficulty && filters?.difficulty !== 'all') {
          query = query?.eq('difficulty', filters?.difficulty);
        }

        // Apply event type filter
        if (filters?.eventType && filters?.eventType !== 'all') {
          query = query?.eq('event_type', filters?.eventType);
        }

        // Apply date range filters
        if (filters?.dateFrom) {
          query = query?.gte('event_date', filters?.dateFrom);
        }

        if (filters?.dateTo) {
          query = query?.lte('event_date', filters?.dateTo);
        }

        // Apply max price filter (price_own_board column)
        if (filters?.maxPrice !== undefined && filters?.maxPrice !== null) {
          query = query?.lte('price_own_board', filters?.maxPrice);
        }

        const { data, error: queryError } = await query;

        if (queryError) {
          throw queryError;
        }

        // Transform data to match React naming conventions
        const transformedEvents = data?.map(event => ({
          id: event?.id,
          organizerId: event?.organizer_id,
          title: event?.title,
          description: event?.description,
          date: event?.event_date,
          time: event?.event_time,
          duration: event?.duration,
          difficulty: event?.difficulty,
          eventType: event?.event_type,
          status: event?.status,
          location: event?.location,
          city: event?.city,
          latitude: event?.latitude,
          longitude: event?.longitude,
          priceOwnBoard: parseFloat(event?.price_own_board),
          priceWithRental: parseFloat(event?.price_with_rental),
          maxParticipants: event?.max_participants,
          minParticipants: event?.min_participants,
          registrationDeadline: event?.registration_deadline,
          groupDiscount: event?.group_discount,
          earlyBirdDiscount: event?.early_bird_discount,
          cancellationInsurance: event?.cancellation_insurance,
          waitingList: event?.waiting_list,
          autoApprove: event?.auto_approve,
          sendReminders: event?.send_reminders,
          requiredEquipment: event?.required_equipment || {},
          skillLevel: event?.skill_level,
          weatherConditions: event?.weather_conditions || {},
          images: event?.images || [],
          // Extract first image for thumbnail
          image: extractFirstImage(event?.images),
          imageAlt: `${event?.title} - ${event?.event_type} etkinlik görseli`,
          attendeeCount: event?.attendee_count || 0,
          createdAt: event?.created_at,
          updatedAt: event?.updated_at
        }));

        setEvents(transformedEvents);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(err?.message || 'Failed to fetch events');
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredEvents();
  }, [
    filters?.search,
    filters?.city,
    filters?.difficulty,
    filters?.eventType,
    filters?.dateFrom,
    filters?.dateTo,
    filters?.maxPrice
  ]);

  return { events, loading, error };
};

/**
 * Helper function to extract first image URL from images array
 * Handles both plain URLs and JSON object strings
 */
const extractFirstImage = (images) => {
  if (!images || !Array?.isArray(images) || images?.length === 0) {
    return null;
  }

  const firstImage = images?.[0];

  // If it's already a URL string
  if (typeof firstImage === 'string' && firstImage?.startsWith('http')) {
    return firstImage;
  }

  // If it's a JSON string
  if (typeof firstImage === 'string' && firstImage?.startsWith('{')) {
    try {
      const parsed = JSON.parse(firstImage);
      return parsed?.url || null;
    } catch (e) {
      console.error('Failed to parse image JSON:', e);
      return null;
    }
  }

  // If it's already an object
  if (typeof firstImage === 'object' && firstImage?.url) {
    return firstImage?.url;
  }

  return null;
};

export default useEvents;